import { asArrayFirst, toFiniteFirst } from '@cornerstonejs/utils';
import { at, compileConditionAt, createSite, hasNoValue, invalidAt, isUnsafeKey, matchForm, parseTemplate, readOwn, safeFunctionCompilers, unrecognized, } from '../safeFunctions/index.js';
import { ContextProgram, defaultDisplaySetContext, displaySetContextFor, isFixedValue, } from './displaySetContext.js';
import { chainComparators } from './groupInstancesBySplitRules.js';
import { BUILT_IN_SERIES_FUNCTIONS } from './seriesFunctions.js';
import { validateSplitRuleSetEntry } from './splitRuleSet.js';
import { splitRuleSchema } from './splitRuleSchema.js';
const DEFINITION = 'display set rules';
function instanceReader(compile, name, site, field) {
    const read = compile.program.readerAt(compile.layer, name, site, { field });
    const { program } = compile;
    return (instance, context) => read(program.runtimeFrom(context, instance), instance);
}
function compileName(value, site, compile) {
    if (typeof value === 'function') {
        return value;
    }
    if (typeof value !== 'string') {
        return unrecognized(splitRuleSchema.name, value, site);
    }
    return instanceReader(compile, value, site);
}
function compareSortValues(a, b) {
    const x = toFiniteFirst(a);
    const y = toFiniteFirst(b);
    if (x !== undefined && y !== undefined) {
        return x - y;
    }
    if (hasNoValue(a) || hasNoValue(b)) {
        return Number(hasNoValue(a)) - Number(hasNoValue(b));
    }
    const aText = String(asArrayFirst(a));
    const bText = String(asArrayFirst(b));
    return aText < bText ? -1 : aText > bText ? 1 : 0;
}
function compileSortKey(value, site, compile) {
    if (typeof value === 'function') {
        return value;
    }
    if (typeof value === 'string') {
        const read = compile.program.readerAt(compile.layer, value, site);
        const { program } = compile;
        return (a, b, context) => {
            const runtime = program.runtimeFrom(context, [a, b]);
            return compareSortValues(read(runtime, a), read(runtime, b));
        };
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return unrecognized(splitRuleSchema.sortKey, value, site);
    }
    matchForm(splitRuleSchema.sortKey, value, site);
    const name = value.sortFunction;
    const sortFunction = readOwn(compile.sortFunctions, name);
    if (typeof sortFunction !== 'function') {
        return invalidAt(at(site, 'sortFunction'), `unknown sort function "${name}"; known: ${Object.keys(compile.sortFunctions).join(', ') || '(none)'}`);
    }
    return sortFunction;
}
function compileAttributeValue(value, site, compile) {
    const { program, layer } = compile;
    if (typeof value === 'function') {
        const scope = { key: `#attribute:${site.path}`, deps: new Map(), layer };
        return (runtime, instances, extras) => value(instances, runtime.env(scope, instances[0], extras));
    }
    if (isFixedValue(value, false)) {
        return () => value;
    }
    if (typeof value === 'string') {
        const read = program.readerAt(layer, value, site, { place: 'attributes' });
        return (runtime, instances, extras) => read(runtime, instances[0], extras);
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return unrecognized(splitRuleSchema.attributeValue, value, site);
    }
    const [form] = matchForm(splitRuleSchema.attributeValue, value, site);
    const fields = value;
    if (form === 'expression') {
        return program.attributeExpression(layer, fields.expression, at(site, 'expression'));
    }
    const templateSite = at(site, 'template');
    const parts = parseTemplate(fields.template, templateSite).map((segment) => 'literal' in segment
        ? () => segment.literal
        : program.readerAt(layer, segment.name, templateSite, {
            field: segment.field,
            place: 'attributes',
        }));
    return (runtime, instances, extras) => parts
        .map((part) => {
        const text = part(runtime, instances[0], extras);
        return hasNoValue(text) ? '' : String(text);
    })
        .join('');
}
function compileAttributes(attributes, site, compile) {
    if (typeof attributes === 'function') {
        return attributes;
    }
    if (!attributes ||
        typeof attributes !== 'object' ||
        Array.isArray(attributes)) {
        return invalidAt(site, 'expected an object of attribute values, keyed by attribute name, or a function (attributes, options) => Record<string, unknown>', attributes);
    }
    const readers = Object.entries(attributes).map(([key, value]) => {
        const valueSite = at(site, key);
        if (isUnsafeKey(key)) {
            invalidAt(valueSite, `the key "${key}" is not allowed`);
        }
        return [key, compileAttributeValue(value, valueSite, compile)];
    });
    const { program } = compile;
    return (engine, options) => {
        const instances = options.instances ?? [];
        const runtime = program.runtimeFrom({
            series: options.series,
            displaySet: displaySetContextFor(options.series, instances),
        }, instances);
        const extras = {
            splitNumber: options.splitNumber,
            sopClassUids: engine?.sopClassUids,
        };
        const result = {};
        for (const [key, read] of readers) {
            result[key] = instances.length
                ? read(runtime, instances, extras)
                : undefined;
        }
        return result;
    };
}
function compileList(value, site, compileEntry) {
    if (!Array.isArray(value)) {
        return invalidAt(site, 'expected an array', value);
    }
    return value.map((entry, index) => compileEntry(entry, at(site, index)));
}
function compileRule(id, rule, site, program, sortFunctions) {
    matchForm(splitRuleSchema.rule, rule, site);
    const layer = program.layer(id, rule.context, at(site, 'context'));
    const compile = { site, program, layer, sortFunctions };
    const compiled = { id, series: program.seriesHook };
    if (rule.groupId !== undefined) {
        compiled.groupId = rule.groupId;
    }
    if (rule.viewportTypes !== undefined) {
        compiled.viewportTypes = rule.viewportTypes;
    }
    if (rule.matches !== undefined) {
        const matchesSite = {
            ...at(site, 'matches'),
            readName: (name, nameSite, field) => instanceReader(compile, name, nameSite, field),
        };
        compiled.matches = compileConditionAt(rule.matches, matchesSite);
    }
    if (rule.groupBy !== undefined) {
        const groupBy = compileList(rule.groupBy, at(site, 'groupBy'), (entry, s) => compileName(entry, s, compile));
        if (groupBy.length) {
            compiled.groupBy = groupBy;
        }
    }
    if (rule.sortBy !== undefined) {
        const comparators = compileList(rule.sortBy, at(site, 'sortBy'), (entry, s) => compileSortKey(entry, s, compile));
        if (comparators.length) {
            compiled.compareInstances = chainComparators(comparators);
        }
    }
    if (rule.attributes !== undefined) {
        compiled.customAttributes = compileAttributes(rule.attributes, at(site, 'attributes'), compile);
    }
    return compiled;
}
export function compileDisplaySetSplitRules(rules, options = {}) {
    if (!rules || typeof rules !== 'object' || Array.isArray(rules)) {
        throw new Error(`Invalid ${DEFINITION}: the rules must be an object keyed by rule id: ${JSON.stringify(rules)}`);
    }
    const program = new ContextProgram({ ...defaultDisplaySetContext, ...options.displaySetContext }, {
        seriesFunctions: {
            ...BUILT_IN_SERIES_FUNCTIONS,
            ...options.seriesFunctions,
        },
        deniedNames: options.deniedNames,
        site: createSite({ definition: DEFINITION, path: 'displaySetContext' }, splitRuleSchema, safeFunctionCompilers),
    });
    const sortFunctions = options.sortFunctions ?? {};
    const compiledSet = {};
    for (const [id, rule] of Object.entries(rules)) {
        validateSplitRuleSetEntry(id, rule);
        const site = createSite({ definition: DEFINITION, path: `rule '${id}'` }, splitRuleSchema, safeFunctionCompilers);
        const { id: _id, ...compiledRule } = compileRule(id, rule, site, program, sortFunctions);
        compiledSet[id] = { ...compiledRule, priority: rule.priority };
    }
    return compiledSet;
}
